import React, { useEffect, useMemo,useRef, useState } from 'react'
import { IconMinus } from '@tabler/icons-react'
import { persistor,useAppDispatch, useAppSelector } from 'store'
import styled from 'styled-components'

import { useBatchProgressPolling } from 'hooks/useBatchProgressPolling'
import { useTranslationProgressPolling } from 'hooks/useTranslationProgressPolling'
import { cacheProduct,fetchBatchTranslationProgress,selectCachedProduct  } from 'services/productsSlice'
import {   cleanupTranslationProgress,
  clearTranslationJob,selectTranslationProgresses } from 'services/translationsSlice'
import { IBatchProgress } from 'types/products'
import { TRANSLATION_STATUS, TranslationProgress } from 'types/translations'

import { NotificationAccordion } from './NotificationAccordion'
import { NotificationToggleButton } from './NotificationToggleButton'

export const NotificationCenter: React.FC = () => {
  const dispatch = useAppDispatch()
  const translationProgresses = useAppSelector(selectTranslationProgresses)
  const [isOpen, setIsOpen] = useState(false)
  const [tempNotificationId, setTempNotificationId] = useState<string | null>(
    null
  )
  const [expandedNotificationId, setExpandedNotificationId] = useState<
    string | null
  >(null)
  const [isFadingOut, setIsFadingOut] = useState(false)
  const [fadeKey, setFadeKey] = useState(0)
  const fadeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tempNotificationRef = useRef<HTMLDivElement | null>(null)
  const isHoveredRef = useRef(false)
  const previousNotificationIdsRef = useRef<Set<string>>(new Set())
  const shownTempNotificationsRef = useRef<Set<string>>(new Set())
  const currentTempNotificationIdRef = useRef<string | null>(null)
  const [readNotificationIds, setReadNotificationIds] = useState<Set<string>>(
    new Set()
  )
  const [isReadStateLoaded, setIsReadStateLoaded] = useState(false)
  const hasCheckedInitialStateRef = useRef(false)
  const hadPreviousIdsInLocalStorageRef = useRef(false)
  const isInitializedRef = useRef(false)
  const isOpenRef = useRef(false)

  // Helper functions to reduce redundancy
  const clearFadeTimeout = () => {
    if (fadeTimeoutRef.current) {
      clearTimeout(fadeTimeoutRef.current)
      fadeTimeoutRef.current = null
    }
  }

  const clearTempNotification = () => {
    setTempNotificationId(null)
    currentTempNotificationIdRef.current = null
    setIsFadingOut(false)
  }

  const saveNotificationStateToLocalStorage = () => {
    try {
      const shownArray = Array.from(shownTempNotificationsRef.current)

      localStorage.setItem(
        'notification-center-shown',
        JSON.stringify(shownArray)
      )

      const previousArray = Array.from(previousNotificationIdsRef.current)

      localStorage.setItem(
        'notification-center-previous',
        JSON.stringify(previousArray)
      )
    } catch (e) {
      console.warn(e)
    }
  }

  useEffect(() => {
    if (isInitializedRef.current) return
    isInitializedRef.current = true

    setIsOpen(false)
    try {
      localStorage.removeItem('notification-center-open')
    } catch (e) {
      console.warn(e)
    }

    try {
      const savedShown = localStorage.getItem('notification-center-shown')

      if (savedShown) {
        try {
          const shownIds = JSON.parse(savedShown) as string[]

          shownTempNotificationsRef.current = new Set(shownIds)
        } catch (e) {
          shownTempNotificationsRef.current = new Set()
        }
      }

      const savedPrevious = localStorage.getItem('notification-center-previous')

      if (savedPrevious) {
        try {
          const previousIds = JSON.parse(savedPrevious) as string[]

          previousNotificationIdsRef.current = new Set(previousIds)
          hadPreviousIdsInLocalStorageRef.current = true
        } catch (e) {
          hadPreviousIdsInLocalStorageRef.current = false
        }
      } else {
        hadPreviousIdsInLocalStorageRef.current = false
        shownTempNotificationsRef.current.clear()
        try {
          localStorage.removeItem('notification-center-shown')
        } catch (e) {
          console.warn(e)
        }
      }

      const savedRead = localStorage.getItem('notification-center-read')

      if (savedRead) {
        try {
          const readIds = JSON.parse(savedRead) as string[]

          setReadNotificationIds(new Set(readIds))
        } catch (e) {
          console.warn(e)
        }
      }

      setIsReadStateLoaded(true)
      hasCheckedInitialStateRef.current = true
    } catch (e) {
      setIsReadStateLoaded(true)
      hasCheckedInitialStateRef.current = true
      console.error('[NotificationCenter] Initialization error', e)
    }

    return () => {
      clearFadeTimeout()
    }
  }, [])

  useEffect(() => {
    try {
      const readArray = Array.from(readNotificationIds)

      localStorage.setItem(
        'notification-center-read',
        JSON.stringify(readArray)
      )
    } catch (e) {
      // Ignore localStorage errors
    }
  }, [readNotificationIds])

  useEffect(() => {
    if (!isInitializedRef.current || !hasCheckedInitialStateRef.current) {
      return
    }

    if (
      translationProgresses.length === 0 &&
      previousNotificationIdsRef.current.size > 0
    ) {
      setTempNotificationId(null)
      setIsFadingOut(false)
      previousNotificationIdsRef.current = new Set()
      setReadNotificationIds(new Set())
      try {
        localStorage.removeItem('notification-center-previous')
        localStorage.removeItem('notification-center-shown')
        localStorage.removeItem('notification-center-read')
      } catch (e) {
        console.warn(e)
      }
      clearFadeTimeout()

      return
    }

    if (
      translationProgresses.length > 0 &&
      previousNotificationIdsRef.current.size === 0 &&
      hadPreviousIdsInLocalStorageRef.current
    ) {
      previousNotificationIdsRef.current = new Set(
        translationProgresses.map((p: TranslationProgress) => p.id)
      )
      try {
        const previousArray = Array.from(previousNotificationIdsRef.current)

        localStorage.setItem(
          'notification-center-previous',
          JSON.stringify(previousArray)
        )
      } catch (e) {
        console.warn(e)
      }
    }

    const currentIds = new Set(translationProgresses.map(p => p.id))
    const previousIds = previousNotificationIdsRef.current

    let newNotificationId: string | null = null

    for (const progress of translationProgresses) {
      const isNew = !previousIds.has(progress.id)
      const notShown = !shownTempNotificationsRef.current.has(progress.id)

      if (isNew) {
        newNotificationId = progress.id
        break
      } else if (notShown) {
        newNotificationId = progress.id
        break
      }
    }

    if (
      translationProgresses.length > 0 &&
      previousNotificationIdsRef.current.size === 0 &&
      !hadPreviousIdsInLocalStorageRef.current &&
      !newNotificationId
    ) {
      previousNotificationIdsRef.current = new Set(
        translationProgresses.map((p: TranslationProgress) => p.id)
      )
      translationProgresses.forEach(p => {
        shownTempNotificationsRef.current.add(p.id)
      })
    }

    const shouldShowTempNotification =
      !isOpenRef.current &&
      newNotificationId &&
      !shownTempNotificationsRef.current.has(newNotificationId)

    if (shouldShowTempNotification && newNotificationId) {
      shownTempNotificationsRef.current.add(newNotificationId)
      previousNotificationIdsRef.current = currentIds

      clearFadeTimeout()

      setTempNotificationId(newNotificationId)
      currentTempNotificationIdRef.current = newNotificationId
      setIsFadingOut(false)
      setFadeKey(prev => prev + 1)
      isHoveredRef.current = false

      fadeTimeoutRef.current = setTimeout(() => {
        if (
          !isHoveredRef.current &&
          currentTempNotificationIdRef.current === newNotificationId
        ) {
          setIsFadingOut(true)
          setFadeKey(prev => prev + 1)
          setTimeout(() => {
            if (currentTempNotificationIdRef.current === newNotificationId) {
              clearTempNotification()
            }
          }, 300)
        }
      }, 3000)
    } else {
      previousNotificationIdsRef.current = currentIds
    }

    saveNotificationStateToLocalStorage()
  }, [translationProgresses])

  useEffect(() => {
    isOpenRef.current = isOpen

    try {
      localStorage.setItem('notification-center-open', String(isOpen))
    } catch (e) {
      console.warn(e)
    }

    if (isOpen) {
      clearTempNotification()
      clearFadeTimeout()

      setReadNotificationIds(prevRead => {
        const newlyRead = new Set(prevRead)
        let hasChanges = false

        translationProgresses.forEach(progress => {
          if (
            !newlyRead.has(progress.id) &&
            (progress.status === TRANSLATION_STATUS.completed ||
              progress.status === TRANSLATION_STATUS.failed)
          ) {
            newlyRead.add(progress.id)
            hasChanges = true
          }
        })

        return hasChanges ? newlyRead : prevRead
      })
    }
  }, [isOpen, translationProgresses])

  const handleToggle = () => {
    setIsOpen(prev => !prev)
    if (isOpen) {
      setExpandedNotificationId(null)
    }
  }

  const handleHeaderClick = () => {
    setIsOpen(false)
  }

  const handleTempNotificationClick = () => {
    if (tempNotificationId) {
      setIsOpen(true)
    }
  }

  const handleTempNotificationHover = (isHovering: boolean) => {
    isHoveredRef.current = isHovering
    if (isHovering) {
      clearFadeTimeout()
      if (isFadingOut) {
        setIsFadingOut(false)
      }
    } else {
      const currentId = currentTempNotificationIdRef.current

      if (currentId && !isFadingOut) {
        fadeTimeoutRef.current = setTimeout(() => {
          if (
            !isHoveredRef.current &&
            currentTempNotificationIdRef.current === currentId
          ) {
            setIsFadingOut(true)
            setTimeout(() => {
              if (currentTempNotificationIdRef.current === currentId) {
                clearTempNotification()
              }
            }, 300)
          }
        }, 3000)
      }
    }
  }

  const handleRemoveNotification = async (progressId: string) => {
    const progress = translationProgresses.find(
      p => p.id === progressId
    ) as TranslationProgress

    if (!progress) return

    try {
      if (
        progress.status === TRANSLATION_STATUS.completed ||
        progress.status === TRANSLATION_STATUS.failed
      ) {
        try {
          await dispatch(cleanupTranslationProgress(progress.id))
        } catch (error) {
          console.error('Failed to cleanup translation progress:', error)
        }
      }
      dispatch(clearTranslationJob(progress.id))

      setReadNotificationIds(prevRead => {
        const updated = new Set(prevRead)

        updated.delete(progressId)

        try {
          const readArray = Array.from(updated)

          localStorage.setItem(
            'notification-center-read',
            JSON.stringify(readArray)
          )
        } catch (e) {
          // Ignore localStorage errors
        }

        return updated
      })

      shownTempNotificationsRef.current.delete(progressId)
      previousNotificationIdsRef.current.delete(progressId)

      saveNotificationStateToLocalStorage()

      const remainingCount = translationProgresses.filter(
        p => p.id !== progressId
      ).length

      if (remainingCount === 0) {
        setIsOpen(false)
        try {
          localStorage.removeItem('notification-center-open')
        } catch (e) {
          console.warn(e)
        }
      }
    } catch (error) {
      console.error('Error removing notification:', error)
    }
  }

  const handleClearAll = async () => {
    try {
      const cleanupPromises = translationProgresses
        .filter(
          p =>
            p.status === TRANSLATION_STATUS.completed ||
            p.status === TRANSLATION_STATUS.failed
        )
        .map(progress => dispatch(cleanupTranslationProgress(progress.id)))

      await Promise.allSettled(cleanupPromises)

      await dispatch(clearTranslationJob()).unwrap()
      await persistor.flush()
      setReadNotificationIds(new Set())
      previousNotificationIdsRef.current = new Set()
      shownTempNotificationsRef.current = new Set()

      clearTempNotification()
      clearFadeTimeout()
      setIsOpen(false)

      try {
        localStorage.removeItem('notification-center-read')
        localStorage.removeItem('notification-center-previous')
        localStorage.removeItem('notification-center-shown')
        localStorage.removeItem('notification-center-open')
      } catch (e) {
        // Ignore localStorage errors
      }
    } catch (error) {
      console.error('Error clearing all notifications:', error)
    }
  }

  const unseenCompletedCount = useMemo(() => {
    if (!isReadStateLoaded || !hasCheckedInitialStateRef.current) {
      return 0
    }

    const count = translationProgresses.filter(
      progress =>
        (progress.status === TRANSLATION_STATUS.completed ||
          progress.status === TRANSLATION_STATUS.failed) &&
        !readNotificationIds.has(progress.id)
    ).length

    return count
  }, [translationProgresses, readNotificationIds, isReadStateLoaded])

  const tempNotification = tempNotificationId
    ? translationProgresses.find(p => p.id === tempNotificationId)
    : null

  const hasNotifications = translationProgresses.length > 0

  return (
    <>
      {hasNotifications && (
        <NotificationToggleButton
          onClick={handleToggle}
          badgeCount={unseenCompletedCount}
          isOpen={isOpen}
        />
      )}
      {isOpen && hasNotifications && (
        <NotificationCenterPanel>
          <Header onClick={handleHeaderClick}>
            <HeaderTitle>Notifications</HeaderTitle>
            <HeaderActions>
              <CloseButton
                onClick={handleToggle}
                aria-label='Close notifications'
              >
                <IconMinus size={20} />
              </CloseButton>
            </HeaderActions>
          </Header>
          <AccordionList>
            {translationProgresses.map(progress => (
              <NotificationItem
                key={progress.id}
                progress={progress}
                onRemove={() => handleRemoveNotification(progress.id)}
                defaultExpanded={progress.id === expandedNotificationId}
              />
            ))}
          </AccordionList>
          <Footer>
            <ClearAllButton
              onClick={handleClearAll}
              aria-label='Clear all notifications'
            >
              Clear All
            </ClearAllButton>
          </Footer>
        </NotificationCenterPanel>
      )}
      {!isOpen && hasNotifications && (
        <HiddenNotificationsContainer>
          {translationProgresses.map(progress => (
            <NotificationItem
              key={progress.id}
              progress={progress}
              onRemove={() => handleRemoveNotification(progress.id)}
              defaultExpanded={false}
            />
          ))}
        </HiddenNotificationsContainer>
      )}
      {!isOpen && tempNotification && hasNotifications && (
        <TemporaryNotification
          ref={tempNotificationRef}
          key={`${tempNotification.id}-${fadeKey}-${
            isFadingOut ? 'fade' : 'show'
          }`}
          $isFadingOut={isFadingOut}
          onMouseEnter={() => handleTempNotificationHover(true)}
          onMouseLeave={() => handleTempNotificationHover(false)}
          onClick={handleTempNotificationClick}
        >
          <NotificationItem
            progress={tempNotification}
            onRemove={() => handleRemoveNotification(tempNotification.id)}
            defaultExpanded={false}
          />
        </TemporaryNotification>
      )}
    </>
  )
}

interface NotificationItemProps {
  progress: TranslationProgress
  onRemove: () => void
  defaultExpanded?: boolean
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  progress,
  onRemove,
  defaultExpanded = false
}) => {
  const dispatch = useAppDispatch()
  const isBatch = progress.productId?.startsWith('batch_')

  // For single product translations
  const { translationProgress: singleProgress } = useTranslationProgressPolling(
    {
      translationId: !isBatch ? progress.id : undefined,
      enabled: !isBatch,
      interval: 3500
    }
  )

  // For batch translations
  const { batchProgress } = useBatchProgressPolling({
    batchId: isBatch ? progress.id : undefined,
    enabled: isBatch,
    interval: 5000
  })

  const currentProgress = isBatch ? batchProgress : singleProgress

  // Get product data
  const [firstProductId, setFirstProductId] = useState<string | null>(null)

  useEffect(() => {
    const fetchFirstProductId = async () => {
      if (!isBatch || firstProductId || !progress.id) return

      try {
        const action = await dispatch(
          fetchBatchTranslationProgress(progress.id)
        )

        if (action.meta.requestStatus === 'fulfilled') {
          const response = action.payload as
            | { data?: IBatchProgress }
            | IBatchProgress
          const batchProgressData: IBatchProgress | undefined =
            'data' in response && response.data
              ? response.data
              : (response as IBatchProgress)

          if (
            batchProgressData?.productIds &&
            Array.isArray(batchProgressData.productIds) &&
            batchProgressData.productIds.length > 0
          ) {
            const productId = batchProgressData.productIds[0]

            setFirstProductId(productId)
            dispatch(cacheProduct(productId))
          }
        }
      } catch (error) {
        console.error('Failed to fetch batch progress for product IDs:', error)
      }
    }

    fetchFirstProductId()
  }, [isBatch, progress.id, dispatch, firstProductId])

  const productId = isBatch
    ? firstProductId
    : currentProgress?.productId || progress.productId

  const productData = useAppSelector(selectCachedProduct(productId || ''))

  useEffect(() => {
    if (productId && !productData && !isBatch) {
      try {
        dispatch(cacheProduct(productId))
      } catch (error) {
        console.error('Failed to cache product:', error)
      }
    }
  }, [productId, productData, isBatch, dispatch])

  if (!currentProgress) {
    return null
  }

  const enhancedProgress = {
    ...currentProgress,
    displayName: productData?.name || currentProgress.productId
  }

  return (
    <NotificationAccordion
      progressData={enhancedProgress}
      productData={productData}
      isBatch={isBatch}
      displayName={enhancedProgress.displayName}
      onRemove={onRemove}
      defaultExpanded={defaultExpanded}
    />
  )
}

const NotificationCenterPanel = styled.div`
  position: fixed;
  bottom: 90px;
  right: 20px;
  width: 400px;
  max-height: 70vh;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadows.menu};
  border: 1px solid ${({ theme }) => theme.colors.muiBorder};
  z-index: ${({ theme }) => theme.zIndex.modal + 40};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideIn 0.3s ease-out;
  font-family: ${({ theme }) => theme.typography.fontFamily};

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`

const Header = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.muiBorder};
  background: ${({ theme }) => theme.colors.background};
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.menuHover};
  }
`

const HeaderTitle = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizeBig};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  color: ${({ theme }) => theme.colors.menuLabel};
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ClearAllButton = styled.button`
  padding: 6px 12px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  font-size: ${({ theme }) => theme.typography.fontSizeSmall};
  font-weight: ${({ theme }) => theme.typography.fontWeightRegular};
  color: ${({ theme }) => theme.colors.muiLabel};
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.menuHover};
    color: ${({ theme }) => theme.colors.menuLabel};
  }
`

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  border-radius: 4px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.muiLabel};
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: ${({ theme }) => theme.colors.menuHover};
    color: ${({ theme }) => theme.colors.menuLabel};
  }
`

const AccordionList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px;
`

const Footer = styled.div`
  padding: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.muiBorder};
  background: ${({ theme }) => theme.colors.background};
  display: flex;
  align-items: center;
  justify-content: flex-end;
`

const HiddenNotificationsContainer = styled.div`
  position: absolute;
  visibility: hidden;
  pointer-events: none;
  width: 0;
  height: 0;
  overflow: hidden;
`

const TemporaryNotification = styled.div<{ $isFadingOut: boolean }>`
  position: fixed;
  bottom: 90px;
  right: 20px;
  width: 400px;
  background: transparent;
  z-index: ${({ theme }) => theme.zIndex.modal + 45};
  cursor: pointer;
  animation: ${({ $isFadingOut }) =>
    $isFadingOut ? 'fadeOut 0.3s ease-out forwards' : 'slideIn 0.3s ease-out'};

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes fadeOut {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
    }
  }

  &:hover {
    animation-play-state: paused;
  }
`
